import { $ } from '../../../shared/js/utils/dom';
import { isMobile } from '../../../shared/js/globals/breakpoints';
import { Accordion } from '@3pc/accordion';
import Bus from '../../../shared/js/modules/event-bus';
import { initRedirects } from '../../_accessibility/focus-redirect';

class Header {
  constructor(params) {
    this.el = params.el;
    this.mobileMainNavToggle = $('.header__nav-main-burger', this.el);
    this.mobileMainNav = $('.header__nav-main-burger-content', this.el);
    this.mobileMainNavInner = $(
      '.header__nav-main-burger-content-inner',
      this.mobileMainNav
    );
    this.mobileMainNavShowerInstance = undefined;
    this.height = this.el.clientHeight;
    this.windowPosTop = 0;
    this.navOpen = false;
    this.isSticky = false;
    this.isStickyVisible = false;
  }

  init() {
    if (!this.el) return;
    this.setupBurgerMenuShower();
    this.headerFocusHandler();
    this.eventBinding();
    this.updateHeaderHeight();
  }

  eventBinding() {
    Bus.subscribe('resize', 'HeaderResize', this.onResize.bind(this));
    Bus.subscribe('scroll', 'HeaderScroll', this.onScroll.bind(this));
    Bus.subscribe(
      'navmain_open',
      'HeaderNavMainOpen',
      this.indicateOpenNavMain.bind(this)
    );
    Bus.subscribe(
      'navmain_close',
      'HeaderNavMainClose',
      this.indicateClosedNavMain.bind(this)
    );
  }

  // Main Nav
  indicateOpenNavMain() {
    this.navOpen = true;
    this.el.classList.add('header--nav-main-open');
  }

  indicateClosedNavMain() {
    this.navOpen = false;
    this.el.classList.remove('header--nav-main-open');
  }

  // Mobile Burger Menu
  setupBurgerMenuShower() {
    if (!this.mobileMainNavToggle) return;
    this.mobileMainNavToggle.removeEventListener(
      'keydown',
      this.onKeydown.bind(this)
    );
    this.mobileMainNav.removeEventListener(
      'keydown',
      this.onKeydown.bind(this)
    );
    this.mobileMainNavShowerInstance?.destroy();

    // IF mobile header
    if (isMobile) {
      const handler = this.mobileMainNavToggle;
      const target = this.mobileMainNav;
      const targetInner = this.mobileMainNavInner;

      if (!handler || !target || !targetInner) return;

      this.mobileMainNavShowerInstance = new Accordion({
        target,
        targetInner,
        handler,
        duration: 300,
        initOpened: false,
        openClassName: 'is-open',
        toggleStart: (_, state) => {
          if (state === 'show') {
            this.indicateOpenNavMain();
            Bus.broadcast('navmain_open', null);
          }
          if (state !== 'show') {
            this.mobileMainNavInner.style.maxHeight = '';
            this.mobileMainNavInner.style.overflowY = '';
          }
        },
        toggleEnd: (_, state) => {
          if (state !== 'show') {
            this.indicateOpenNavMain();
            Bus.broadcast('navmain_close-below-level', {
              belowLevel: 1,
            });
            Bus.broadcast('navmain_close', null);
          }
          if (state === 'show') {
            this.mobileMainNavInner.style.maxHeight = `${
              window.innerHeight - this.height
            }px`;
            this.mobileMainNavInner.style.overflowY = 'auto';
          }
        },
      });

      this.mobileMainNavShowerInstance.init();
      this.el.classList.add('header--is-reduced');
      this.mobileMainNavToggle.addEventListener(
        'keydown',
        this.onKeydown.bind(this)
      );
      this.mobileMainNav.addEventListener('keydown', this.onKeydown.bind(this));
      return;
    }

    this.el.classList.remove('header--is-reduced');
  }

  // Sticky Header
  updateHeaderHeight() {
    this.height = this.el.clientHeight;
  }

  stickyHeaderHandler() {
    if (this.navOpen) return;

    const currentPos = window.pageYOffset;
    const isScrollingDown = this.windowPosTop < currentPos;

    // Hide initial transition, when switching to fixed by using opacity.
    const removeSticky = () => {
      this.isSticky = false;
      this.el.classList.remove('header--is-fixed');
      removeVisibility();
      this.el.style.opacity = '';
      document.body.style.paddingTop = '';
    };
    const addSticky = () => {
      this.isSticky = true;
      this.el.classList.add('header--is-fixed');
      document.body.style.paddingTop = `${this.height}px`;
    };
    const addVisibility = () => {
      this.el.style.opacity = '1';
      this.isStickyVisible = true;
      this.el.classList.add('header--is-fixed-visible');
    };
    const removeVisibility = () => {
      this.el.style.opacity = '0';
      this.isStickyVisible = false;
      this.el.classList.remove('header--is-fixed-visible');
    };

    this.windowPosTop = currentPos;

    if (this.windowPosTop >= this.height) addSticky();
    if (this.windowPosTop < this.height) removeSticky();

    if (this.isSticky && isScrollingDown) removeVisibility();
    if (this.isSticky && !isScrollingDown) addVisibility();
  }

  // Focus Handling
  headerFocusHandler() {
    // Collect elements
    const mobileMainNavToggle = this.mobileMainNavToggle;
    const mainNav = this.el.querySelector('.header__nav-main');
    const featuresMetaNavItems = this.el.querySelectorAll( // eslint-disable-line
      '.header__features .header-nav-meta__item'
    );
    const featuresMetaNavLastItem =
      featuresMetaNavItems[featuresMetaNavItems.length - 1];
    const quicklinksToggle = this.el.querySelector(
      '.header-quicklinks__toggle, .header-quicklink'
    );

    // Element configs
    const mobileMainNavToggleConfig =
      "{ 'smartphone': {'next': { 'selector': '.nav-main__link' }, 'previous': { 'selector': '.header__features .header-nav-meta__item:last-child button, .header__features .header-nav-meta__item:last-child a' } }, 'desktop': {} }";
    const mainNavConfig =
      "{ 'smartphone': { 'next': { 'onFocusout': true, 'selector': '.header-quicklinks__toggle,.header-quicklink' } }, 'desktop': {} }";
    const featuresMetaNavLastItemConfig =
      "{ 'smartphone': { 'next': { 'selector': '.header__nav-main-burger' } }, 'desktop': {} }";
    const quicklinksToggleConfig =
      "{ 'smartphone': { 'previous': { 'selectLastElement': true, 'selector': '.nav-main [aria-hidden=`false`] .nav-main__link, .nav-main__item[data-level=`1`] > .nav-main__expand-toggle' } }, 'desktop': {} }";

    // Write config on element
    mobileMainNavToggle?.setAttribute(
      'data-focus-redirect',
      mobileMainNavToggleConfig
    );
    mainNav?.setAttribute('data-focus-redirect', mainNavConfig);
    quicklinksToggle?.setAttribute(
      'data-focus-redirect',
      quicklinksToggleConfig
    );
    featuresMetaNavLastItem?.setAttribute(
      'data-focus-redirect',
      featuresMetaNavLastItemConfig
    );

    // Init redirect on elements in accordance with their config
    initRedirects([
      mobileMainNavToggle,
      mainNav,
      quicklinksToggle,
      featuresMetaNavLastItem,
    ]);
  }

  // Event handler
  onResize() {
    this.stickyHeaderHandler();
    this.setupBurgerMenuShower();
    this.updateHeaderHeight();
  }

  onScroll() {
    this.stickyHeaderHandler();
  }

  onKeydown(e) {
    if (e.key === 'Escape') {
      this.mobileMainNavShowerInstance.close();
      this.mobileMainNavToggle.focus();
    }
  }
}

const header = $('.header');
let headerInstance;

if (header) {
  headerInstance = new Header({
    el: $('.header'),
  });
  headerInstance.init();
}

const getHeaderHeight = () => {
  headerInstance.updateHeaderHeight();
  return headerInstance.height;
};

const getHeaderHeightPaddingBottom = () => {
  return parseInt(
    window
      .getComputedStyle(headerInstance.el)
      .getPropertyValue('padding-bottom')
      .replace('px', ''),
    10
  );
};

const getHeaderIsSticky = () => {
  return headerInstance.isSticky;
};

export { getHeaderHeight, getHeaderHeightPaddingBottom, getHeaderIsSticky };
